import { callLambdaFunction, retryWithExponentialBackoff } from './aiClient';

const CHAT_COMPLETION_ENDPOINT = import.meta.env?.VITE_AWS_LAMBDA_CHAT_COMPLETION_URL;

/**
 * Perform web search using Perplexity API for marketing research
 * @param {string} query - Search query
 * @param {object} options - Search options
 * @returns {Promise<{content: string, sources: array, metadata: object}>}
 */
export async function searchWeb(query, options = {}) {
  try {
    const {
      searchDepth = 'medium', // 'low', 'medium', 'high'
      maxTokens = 2048,
      temperature = 0.7,
      includeLocation = false,
      userLocation = null
    } = options;

    const messages = [
      {
        role: 'system',
        content: 'You are a marketing research assistant. Provide comprehensive, well-sourced answers with specific examples, statistics, and actionable insights. Always cite your sources.'
      },
      {
        role: 'user',
        content: query
      }
    ];

    const payload = {
      provider: 'PERPLEXITY',
      model: 'perplexity/sonar-pro',
      messages,
      stream: false,
      parameters: {
        temperature,
        max_tokens: maxTokens,
        web_search_options: {
          search_context_size: searchDepth
        }
      }
    };

    // Add location context if provided
    if (includeLocation && userLocation) {
      payload.parameters.web_search_options.user_location = userLocation;
    }

    const response = await callLambdaFunction(
      CHAT_COMPLETION_ENDPOINT, 
      payload,
      {
        provider: 'perplexity',
        endpoint: '/chat/completions',
        metadata: { model: 'perplexity/sonar-pro', searchDepth, query: query?.substring(0, 100) }
      }
    );
    
    const content = response?.choices?.[0]?.message?.content || '';
    
    // Extract sources from response (Perplexity includes citations)
    const sources = extractSources(content);
    
    return {
      content,
      sources,
      metadata: {
        query,
        searchDepth,
        timestamp: new Date()?.toISOString(),
        model: 'perplexity/sonar-pro',
        usage: response?.usage
      }
    };
  } catch (error) {
    console.error('Perplexity Search Error:', error);
    throw error;
  }
}

/**
 * Perform streaming web search using Perplexity API
 * @param {string} query - Search query
 * @param {function} onChunk - Callback for each content chunk
 * @param {function} onComplete - Callback when search completes
 * @param {function} onError - Callback for errors
 * @param {object} options - Search options
 */
export async function searchWebStreaming(query, onChunk, onComplete, onError, options = {}) {
  try {
    const {
      searchDepth = 'medium',
      maxTokens = 2048,
      temperature = 0.7
    } = options;

    const messages = [
      {
        role: 'system',
        content: 'You are a marketing research assistant. Provide comprehensive, well-sourced answers with specific examples, statistics, and actionable insights. Always cite your sources.'
      },
      {
        role: 'user',
        content: query
      }
    ];

    const payload = {
      provider: 'PERPLEXITY',
      model: 'perplexity/sonar-pro',
      messages,
      stream: true,
      parameters: {
        temperature,
        max_tokens: maxTokens,
        web_search_options: {
          search_context_size: searchDepth
        }
      }
    };

    // Use retry mechanism for fetch call
    const response = await retryWithExponentialBackoff(async () => {
      const res = await fetch(CHAT_COMPLETION_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!res?.ok) {
        const error = new Error(`HTTP error! status: ${res.status}`);
        error.response = { status: res?.status };
        throw error;
      }

      return res;
    });

    const reader = response?.body?.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullContent = '';

    while (true) {
      const { done, value } = await reader?.read();
      if (done) break;

      buffer += decoder?.decode(value, { stream: true });
      const lines = buffer?.split('\n');
      buffer = lines?.pop() || '';

      for (const line of lines) {
        if (line?.startsWith('data: ')) {
          const data = line?.slice(6);
          if (data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);
            if (parsed?.type === 'chunk') {
              const content = parsed?.chunk?.choices?.[0]?.delta?.content || '';
              if (content) {
                fullContent += content;
                onChunk(content);
              }
            } else if (parsed?.type === 'error') {
              onError(new Error(parsed.error));
              return;
            }
          } catch (e) {
            console.error('Error parsing SSE data:', e);
          }
        }
      }
    }

    const sources = extractSources(fullContent);
    
    onComplete({
      content: fullContent,
      sources,
      metadata: {
        query,
        searchDepth,
        timestamp: new Date()?.toISOString(),
        model: 'perplexity/sonar-pro'
      }
    });
  } catch (error) {
    console.error('Streaming Perplexity Search Error:', error);
    onError(error);
  }
}

/**
 * Extract source citations from Perplexity response
 * @param {string} content - Response content
 * @returns {array} Array of source objects
 */
function extractSources(content) {
  const sources = [];
  
  // Perplexity typically includes citations in [1], [2] format
  // Extract URLs and references from the content
  const urlRegex = /https?:\/\/[^\s)]+/g;
  const urls = content?.match(urlRegex) || [];
  
  urls?.forEach((url, index) => {
    sources?.push({
      id: index + 1,
      url,
      title: `Source ${index + 1}`
    });
  });
  
  return sources;
}

/**
 * Search for high-performing ads in a specific industry
 * @param {string} industry - Target industry
 * @param {object} options - Search options
 */
export async function searchHighPerformingAds(industry, options = {}) {
  const query = `Find and analyze the most successful and high-performing advertising campaigns in the ${industry} industry. Include specific examples, key metrics, creative strategies, and what made them effective. Focus on recent campaigns from the last 6-12 months.`;
  
  return searchWeb(query, { ...options, searchDepth: 'high' });
}

/**
 * Search for trending content strategies
 * @param {string} platform - Platform (e.g., 'social media', 'email', 'video')
 * @param {object} options - Search options
 */
export async function searchTrendingStrategies(platform = 'all platforms', options = {}) {
  const query = `What are the latest trending content marketing strategies and tactics for ${platform}? Include current best practices, emerging trends, engagement techniques, and real examples from successful brands.`;
  
  return searchWeb(query, { ...options, searchDepth: 'high' });
}

/**
 * Analyze competitor campaigns
 * @param {string} competitor - Competitor name or industry
 * @param {object} options - Search options
 */
export async function analyzeCompetitorCampaigns(competitor, options = {}) {
  const query = `Analyze recent marketing campaigns and strategies from ${competitor}. Include their messaging, channels used, creative approaches, estimated performance, and key takeaways for competitive positioning.`;
  
  return searchWeb(query, { ...options, searchDepth: 'high' });
}

/**
 * Search for marketing statistics and benchmarks
 * @param {string} topic - Topic or metric
 * @param {object} options - Search options
 */
export async function searchMarketingStats(topic, options = {}) {
  const query = `Provide the latest statistics, benchmarks, and data for ${topic} in marketing. Include industry averages, performance metrics, conversion rates, and credible sources for the data.`;
  
  return searchWeb(query, { ...options, searchDepth: 'medium' });
}

export default {
  searchWeb,
  searchWebStreaming,
  searchHighPerformingAds,
  searchTrendingStrategies,
  analyzeCompetitorCampaigns,
  searchMarketingStats
};